import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNotifications } from '../../hooks/useNotifications';
import { 
  CheckCircleIcon, 
  ExclamationCircleIcon, 
  ExclamationTriangleIcon, 
  InformationCircleIcon,
  XMarkIcon 
} from '@heroicons/react/24/outline';

const NotificationsContainer = styled.div`
  position: fixed;
  top: ${props => props.theme.spacing.xl};
  right: ${props => props.theme.spacing.xl};
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  max-width: 400px;
  pointer-events: none;
`;

const NotificationCard = styled.div<{ 
  variant: 'success' | 'error' | 'warning' | 'info';
  $isExiting?: boolean;
}>`
  display: flex;
  align-items: flex-start;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borders.radius.md};
  box-shadow: ${props => props.theme.shadows.lg};
  background-color: white;
  border-left: 4px solid;
  pointer-events: ${props => props.$isExiting ? 'none' : 'auto'};
  cursor: ${props => props.$isExiting ? 'default' : 'pointer'};
  transition: all 0.2s ease;
  
  animation: ${props => props.$isExiting ? 'slideOut' : 'slideIn'} 0.3s ease-out;
  
  &:hover:not([data-exiting="true"]) {
    box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 10px 10px -5px rgb(0 0 0 / 0.04);
    transform: translateY(-2px);
  }
  
  ${props => {
    switch (props.variant) {
      case 'success':
        return `border-left-color: ${props.theme.colors.success[500]};`;
      case 'error':
        return `border-left-color: ${props.theme.colors.error[500]};`;
      case 'warning':
        return `border-left-color: ${props.theme.colors.warning[500]};`;
      default:
        return `border-left-color: ${props.theme.colors.primary[500]};`;
    }
  }}
  
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @keyframes slideOut {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(120%);
      opacity: 0;
    }
  }
`;

const NotificationIcon = styled.div<{ variant: 'success' | 'error' | 'warning' | 'info' }>`
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
  
  ${props => {
    switch (props.variant) {
      case 'success':
        return `color: ${props.theme.colors.success[500]};`;
      case 'error':
        return `color: ${props.theme.colors.error[500]};`;
      case 'warning':
        return `color: ${props.theme.colors.warning[500]};`;
      default:
        return `color: ${props.theme.colors.primary[500]};`;
    }
  }}
`;

const NotificationContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const NotificationTitle = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.theme.colors.gray[900]};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const NotificationMessage = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.gray[600]};
`;

const CloseButton = styled.button`
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.gray[400]};
  transition: color 0.2s ease;
  
  &:hover {
    color: ${props => props.theme.colors.gray[600]};
  }
  
  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const getIcon = (type: 'success' | 'error' | 'warning' | 'info') => {
  switch (type) {
    case 'success':
      return CheckCircleIcon;
    case 'error':
      return ExclamationCircleIcon;
    case 'warning':
      return ExclamationTriangleIcon;
    default:
      return InformationCircleIcon;
  }
};

export function NotificationContainer() {
  const { notifications, dismiss } = useNotifications();
  const [exitingNotifications, setExitingNotifications] = useState<Set<string>>(new Set());

  const animatedDismiss = (id: string) => {
    // Prevent multiple dismissals of the same notification
    if (exitingNotifications.has(id)) {
      return;
    }
    
    // Start exit animation
    setExitingNotifications(prev => new Set(prev).add(id));
    
    // Actually dismiss after animation completes
    setTimeout(() => {
      dismiss(id);
      setExitingNotifications(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }, 300); // Match animation duration
  };

  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];

    notifications.forEach(notification => {
      if (notification.autoClose) {
        const timer = setTimeout(() => {
          animatedDismiss(notification.id);
        }, notification.duration);
        timers.push(timer);
      }
    });

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, [notifications]);

  if (notifications.length === 0) {
    return null;
  }

  return (
    <NotificationsContainer>
      {notifications.map(notification => {
        const IconComponent = getIcon(notification.type);
        const isExiting = exitingNotifications.has(notification.id);
        
        return (
          <NotificationCard 
            key={notification.id} 
            variant={notification.type}
            $isExiting={isExiting}
            data-exiting={isExiting}
            onClick={() => animatedDismiss(notification.id)}
          >
            <NotificationIcon variant={notification.type}>
              <IconComponent />
            </NotificationIcon>
            
            <NotificationContent>
              <NotificationTitle>{notification.title}</NotificationTitle>
              {notification.message && (
                <NotificationMessage>{notification.message}</NotificationMessage>
              )}
            </NotificationContent>
            
            <CloseButton 
              onClick={(e) => {
                e.stopPropagation(); // Prevent double-dismiss
                animatedDismiss(notification.id);
              }}
            >
              <XMarkIcon />
            </CloseButton>
          </NotificationCard>
        );
      })}
    </NotificationsContainer>
  );
}